import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import {
  WholesalerDefinitionPublic,
  WholesalerSetupField,
  WholesalerStoreConfig,
  WholesalerSubstitution,
  WholesalerSubstitutionStrategy,
} from 'src/app/models/invoice-import.model';
import {
  WholesalerCategoryMappingEntry,
  WholesalerEnrichmentConfig,
  WholesalerPantProductEntry,
} from 'src/app/models/invoice-import.model';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import { apiErrorCode, substitutionErrorMessage } from 'src/app/services/invoice-import.util';
import Swal from 'sweetalert2';
import {
  SubstitutionMappingDialogComponent,
  SubstitutionMappingDialogResult,
} from '../substitution-mapping-dialog/substitution-mapping-dialog.component';

function thresholdOrderValidator(group: AbstractControl): ValidationErrors | null {
  const small = Number(group.get('costChangeSmallPercent')?.value);
  const notice = Number(group.get('costChangeNoticePercent')?.value);
  const large = Number(group.get('costChangeLargePercent')?.value);
  if ([small, notice, large].some((n) => Number.isNaN(n))) {
    return null;
  }
  if (small > notice || notice > large) {
    return { thresholdOrder: true };
  }
  return null;
}

@Component({
  selector: 'app-wholesaler-config-dialog',
  templateUrl: './wholesaler-config-dialog.component.html',
  styleUrls: ['./wholesaler-config-dialog.component.scss'],
})
export class WholesalerConfigDialogComponent implements OnInit {
  @Input() storeId!: string;
  @Input() definition!: WholesalerDefinitionPublic;
  @Input() config?: WholesalerStoreConfig;

  form!: FormGroup;
  fieldErrors: Record<string, string> = {};
  addressInput = '';
  approvedAddresses: string[] = [];
  categoryRows: WholesalerCategoryMappingEntry[] = [];
  pantRows: WholesalerPantProductEntry[] = [];
  storeCategories: { _id: string; name: string }[] = [];
  pantProductSearch: Record<number, string> = {};
  pantProductResults: Record<number, any[]> = {};
  enrichmentStatus: any = null;
  enrichmentActionError = '';

  substitutionItems: WholesalerSubstitution[] = [];
  substitutionTotal = 0;
  substitutionPage = 1;
  substitutionLimit = 20;
  substitutionShowEnded = false;
  substitutionSearchQ = '';
  substitutionLoadError = '';
  substitutionLoading = false;
  readonly substitutionStrategies: WholesalerSubstitutionStrategy[] = [
    'permanent',
    'one_delivery',
    'until_restock',
  ];

  constructor(
    protected ref: NbDialogRef<WholesalerConfigDialogComponent>,
    private fb: FormBuilder,
    private dialogService: NbDialogService,
    private invoiceImportService: InvoiceImportService,
    private ecommerceService: EcommerceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const routingGroup: Record<string, unknown> = {};
    const fields = this.definition.storeSetupSchema?.fields || [];
    for (const field of fields) {
      const val = this.config?.routing?.[this.routingFieldKey(field)] ?? '';
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }
      if (field.pattern) {
        validators.push(Validators.pattern(field.pattern));
      }
      routingGroup[field.key] = [val, validators];
    }

    const behavior = this.definition.behaviorDefaults || {};
    const cfgBehavior = this.config?.behavior || {};
    const costPriceBasis =
      (cfgBehavior['costPriceBasis'] as string) || (behavior['costPriceBasis'] as string) || 'unit_excl_vat';

    this.form = this.fb.group(
      {
        isActive: [this.config?.isActive ?? true],
        routing: this.fb.group(routingGroup),
        autoApproveSmallChanges: [
          cfgBehavior['autoApproveSmallChanges'] ?? behavior['autoApproveSmallChanges'] ?? true,
        ],
        costChangeSmallPercent: [
          cfgBehavior['costChangeSmallPercent'] ?? behavior['costChangeSmallPercent'] ?? 1,
          [Validators.required, Validators.min(0)],
        ],
        costChangeNoticePercent: [
          cfgBehavior['costChangeNoticePercent'] ?? behavior['costChangeNoticePercent'] ?? 3,
          [Validators.required, Validators.min(0)],
        ],
        costChangeLargePercent: [
          cfgBehavior['costChangeLargePercent'] ?? behavior['costChangeLargePercent'] ?? 8,
          [Validators.required, Validators.min(0)],
        ],
        minimumMarginPercent: [
          cfgBehavior['minimumMarginPercent'] ?? behavior['minimumMarginPercent'] ?? 15,
          [Validators.min(0), Validators.max(100)],
        ],
        marginDropAlertPoints: [
          cfgBehavior['marginDropAlertPoints'] ?? behavior['marginDropAlertPoints'] ?? 5,
          [Validators.min(0)],
        ],
        costPriceBasis: [{ value: costPriceBasis, disabled: true }],
        costChangeEpsilon: [{ value: behavior['costChangeEpsilon'] ?? 0.01, disabled: true }],
        costPriceSource: [
          cfgBehavior['costPriceSource'] ?? behavior['costPriceSource'] ?? 'latest_invoice',
        ],
        countDeviationBlocksAutoApprove: [
          cfgBehavior['countDeviationBlocksAutoApprove'] ??
            behavior['countDeviationBlocksAutoApprove'] ??
            true,
        ],
        creditNoteDefaultKind: [
          cfgBehavior['creditNoteDefaultKind'] ?? behavior['creditNoteDefaultKind'] ?? 'return',
        ],
        enrichmentEnabled: [this.config?.enrichment?.enabled ?? false],
        enrichmentPullMode: [this.config?.enrichment?.pullMode ?? 'list_only'],
        enrichmentProtocol: [this.config?.enrichment?.protocol ?? 'ftp'],
        enrichmentHost: [this.config?.enrichment?.host ?? ''],
        enrichmentPort: [this.config?.enrichment?.port ?? 21],
        enrichmentUsername: [this.config?.enrichment?.username ?? ''],
        enrichmentPassword: [this.config?.enrichment?.password ?? ''],
        enrichmentRemoteDir: [this.config?.enrichment?.remoteDir ?? ''],
        enrichmentFilePattern: [
          this.config?.enrichment?.filePattern ?? '\\.(FAK|SUB|ZIP)$',
        ],
        substitutionDefaultForTemporary: [
          this.config?.substitution?.defaultForTemporary ?? 'until_restock',
        ],
        substitutionPromptOnNewSub: [this.config?.substitution?.promptOnNewSub ?? true],
      },
      { validators: thresholdOrderValidator }
    );

    this.approvedAddresses = [...(this.config?.approvedFromAddresses || [])];
    this.categoryRows = [...(this.config?.categoryMapping || [])];
    this.pantRows = [...(this.config?.pantProducts || [])];
    this.loadStoreCategories();
    if (this.definition?.supplierKey) {
      this.refreshEnrichmentStatus();
    }
    if (this.isDagrofa) {
      this.loadSubstitutions();
    }
  }

  get isDagrofa(): boolean {
    return this.definition?.supplierKey === 'DAGROFA';
  }

  private loadStoreCategories(): void {
    this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.storeCategories = data.categories || data || [];
      },
      error: () => {
        this.storeCategories = [];
      },
    });
  }

  categoryName(categoryId: string): string {
    return this.storeCategories.find((c) => c._id === categoryId)?.name || categoryId;
  }

  addCategoryRow(): void {
    this.categoryRows.push({ codePrefix: '', categoryId: '' });
  }

  removeCategoryRow(i: number): void {
    this.categoryRows.splice(i, 1);
  }

  addPantRow(): void {
    this.pantRows.push({ supplierItemNumber: '', pantClass: 'A', productId: '' });
  }

  removePantRow(i: number): void {
    this.pantRows.splice(i, 1);
  }

  searchPantProduct(i: number): void {
    const q = (this.pantProductSearch[i] || '').trim();
    if (q.length < 2) {
      this.pantProductResults[i] = [];
      return;
    }
    this.ecommerceService
      .getStoreProducts(this.storeId, { page: 1, limit: 8, search: q, adminView: true })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          this.pantProductResults[i] = data.products || [];
        },
        error: () => {
          this.pantProductResults[i] = [];
        },
      });
  }

  pickPantProduct(i: number, product: any): void {
    this.pantRows[i].productId = product._id;
    this.pantProductSearch[i] = product.name;
    this.pantProductResults[i] = [];
  }

  refreshEnrichmentStatus(): void {
    this.invoiceImportService.getWholesalerEnrichmentStatus(this.storeId, this.definition.supplierKey).subscribe({
      next: (res: any) => {
        this.enrichmentStatus = res.response?.data || res.data || null;
      },
      error: () => {
        this.enrichmentStatus = null;
      },
    });
  }

  testEnrichmentConnection(): void {
    this.enrichmentActionError = '';
    this.spinner.show();
    this.invoiceImportService.testWholesalerEnrichment(this.storeId, this.definition.supplierKey).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        if (!data.ok) {
          this.enrichmentActionError = data.error || this.translate.instant('INVOICE_IMPORTS.PHASE2.ENRICHMENT_TEST_FAIL');
          return;
        }
        this.toastrSuccess('INVOICE_IMPORTS.PHASE2.ENRICHMENT_TEST_OK');
      },
      error: (err: any) => {
        this.spinner.hide();
        this.enrichmentActionError = this.formatEnrichmentError(err);
      },
    });
  }

  pullEnrichmentNow(listOnly = false): void {
    this.enrichmentActionError = '';
    this.spinner.show();
    this.invoiceImportService
      .pullWholesalerEnrichment(this.storeId, this.definition.supplierKey, listOnly)
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toastrSuccess('INVOICE_IMPORTS.PHASE2.ENRICHMENT_PULL_OK');
          this.refreshEnrichmentStatus();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.enrichmentActionError = this.formatEnrichmentError(err);
        },
      });
  }

  private formatEnrichmentError(err: any): string {
    const code = apiErrorCode(err);
    const msg = err.error?.response?.message || err.message;
    if (code === 'FTP_PULL_DISABLED') {
      return this.translate.instant('INVOICE_IMPORTS.PHASE2.FTP_PULL_DISABLED');
    }
    if (code === 'SECRET_STORAGE_UNAVAILABLE') {
      return this.translate.instant('INVOICE_IMPORTS.PHASE2.SECRET_STORAGE_UNAVAILABLE');
    }
    return code ? `${msg} (${code})` : msg;
  }

  private toastrSuccess(key: string): void {
    this.toastr.success(this.translate.instant(key));
  }

  costPriceBasisLabel(): string {
    const basis = this.form?.get('costPriceBasis')?.value;
    if (basis === 'unit_incl_vat') {
      return this.translate.instant('INVOICE_IMPORTS.SETTINGS.COST_BASIS_INCL');
    }
    return this.translate.instant('INVOICE_IMPORTS.SETTINGS.COST_BASIS_EXCL');
  }

  routingFieldKey(field: WholesalerSetupField): string {
    const parts = field.key.split('.');
    return parts[parts.length - 1];
  }

  fieldLabel(field: WholesalerSetupField): string {
    const lang = this.translate.currentLang || 'da';
    if (lang === 'da' && field.labelDa) {
      return field.labelDa;
    }
    return field.label;
  }

  fieldHelp(field: WholesalerSetupField): string {
    const lang = this.translate.currentLang || 'da';
    if (lang === 'da' && field.helpDa) {
      return field.helpDa;
    }
    return field.help || '';
  }

  addAddress(): void {
    const v = (this.addressInput || '').trim().toLowerCase();
    if (!v) {
      return;
    }
    if (!this.approvedAddresses.includes(v)) {
      this.approvedAddresses.push(v);
    }
    this.addressInput = '';
  }

  removeAddress(i: number): void {
    this.approvedAddresses.splice(i, 1);
  }

  save(): void {
    this.fieldErrors = {};
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.errors?.['thresholdOrder']) {
        this.fieldErrors['_global'] = this.translate.instant('INVOICE_IMPORTS.SETTINGS.THRESHOLD_ORDER_ERROR');
      }
      return;
    }
    const raw = this.form.getRawValue();
    const routing: Record<string, unknown> = {};
    const fields = this.definition.storeSetupSchema?.fields || [];
    for (const field of fields) {
      const flatKey = field.key.replace(/^routing\./, '');
      routing[flatKey] = raw.routing?.[field.key];
    }

    const enrichment: WholesalerEnrichmentConfig = {
      enabled: !!raw.enrichmentEnabled,
      pullMode: raw.enrichmentPullMode,
      protocol: raw.enrichmentProtocol,
      host: raw.enrichmentHost,
      port: Number(raw.enrichmentPort),
      username: raw.enrichmentUsername,
      remoteDir: raw.enrichmentRemoteDir,
      filePattern: raw.enrichmentFilePattern,
    };
    const pwd = String(raw.enrichmentPassword || '').trim();
    if (pwd && pwd !== '••••') {
      enrichment.password = pwd;
    } else if (pwd === '••••') {
      enrichment.password = '••••';
    }

    const body: Record<string, unknown> = {
      isActive: raw.isActive,
      routing,
      behavior: {
        autoApproveSmallChanges: !!raw.autoApproveSmallChanges,
        costChangeSmallPercent: Number(raw.costChangeSmallPercent),
        costChangeNoticePercent: Number(raw.costChangeNoticePercent),
        costChangeLargePercent: Number(raw.costChangeLargePercent),
        minimumMarginPercent: Number(raw.minimumMarginPercent),
        marginDropAlertPoints: Number(raw.marginDropAlertPoints),
        costPriceBasis: raw.costPriceBasis,
        costChangeEpsilon: Number(raw.costChangeEpsilon),
        costPriceSource: raw.costPriceSource,
        countDeviationBlocksAutoApprove: !!raw.countDeviationBlocksAutoApprove,
        creditNoteDefaultKind: raw.creditNoteDefaultKind,
        newItemTargetCostRatio:
          this.config?.behavior?.['newItemTargetCostRatio'] ??
          this.definition.behaviorDefaults?.['newItemTargetCostRatio'],
        newItemMaxRecommendedMultiple:
          this.config?.behavior?.['newItemMaxRecommendedMultiple'] ??
          this.definition.behaviorDefaults?.['newItemMaxRecommendedMultiple'],
      },
      approvedFromAddresses: this.approvedAddresses,
      categoryMapping: this.categoryRows.filter((r) => r.codePrefix && r.categoryId),
      pantProducts: this.pantRows.filter((r) => r.productId),
      enrichment,
    };
    if (this.isDagrofa) {
      (body as Record<string, unknown>)['substitution'] = {
        defaultForTemporary: raw.substitutionDefaultForTemporary,
        promptOnNewSub: !!raw.substitutionPromptOnNewSub,
      };
    }

    this.spinner.show();
    this.invoiceImportService.putStoreWholesaler(this.storeId, this.definition.supplierKey, body).subscribe({
      next: () => {
        this.spinner.hide();
        this.ref.close(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        const resp = err.error?.response || err.error;
        if (resp?.code === 'ROUTING_ID_IN_USE' || resp?.message?.includes('ROUTING')) {
          this.fieldErrors['_global'] = this.translate.instant('INVOICE_IMPORTS.SETTINGS.ROUTING_IN_USE');
          return;
        }
        if (this.applyWholesalerValidationErrors(resp)) {
          return;
        }
        this.fieldErrors['_global'] = resp?.message || err.message;
      },
    });
  }

  private applyWholesalerValidationErrors(resp: any): boolean {
    const data = resp?.data;
    if (data?.code === 'WHOLESALER_CONFIG_INVALID' && Array.isArray(data.errors)) {
      const map: Record<string, string> = {};
      for (const item of data.errors) {
        if (item?.field) {
          map[item.field] = item.message;
        }
      }
      this.fieldErrors = map;
      return true;
    }
    const fieldErrs = resp?.errors;
    if (fieldErrs && typeof fieldErrs === 'object' && !Array.isArray(fieldErrs)) {
      this.fieldErrors = fieldErrs;
      return true;
    }
    return false;
  }

  loadSubstitutions(): void {
    if (!this.isDagrofa) {
      return;
    }
    this.substitutionLoading = true;
    this.substitutionLoadError = '';
    this.invoiceImportService
      .listSubstitutions(this.storeId, {
        supplierKey: 'DAGROFA',
        active: this.substitutionShowEnded ? 'all' : true,
        q: this.substitutionSearchQ.trim() || undefined,
        page: this.substitutionPage,
        limit: this.substitutionLimit,
      })
      .subscribe({
        next: (res: any) => {
          this.substitutionLoading = false;
          const data = res.response?.data || res.data || {};
          this.substitutionItems = data.items || [];
          this.substitutionTotal = data.total ?? 0;
          this.substitutionPage = data.page ?? this.substitutionPage;
          this.substitutionLimit = data.limit ?? this.substitutionLimit;
        },
        error: (err: any) => {
          this.substitutionLoading = false;
          this.substitutionLoadError = err.error?.response?.message || err.message;
        },
      });
  }

  onSubstitutionSearch(): void {
    this.substitutionPage = 1;
    this.loadSubstitutions();
  }

  toggleSubstitutionShowEnded(): void {
    this.substitutionShowEnded = !this.substitutionShowEnded;
    this.substitutionPage = 1;
    this.loadSubstitutions();
  }

  substitutionStrategyLabel(strategy: WholesalerSubstitutionStrategy): string {
    return this.translate.instant(`INVOICE_IMPORTS.SUB.STRATEGY_${strategy.toUpperCase()}`);
  }

  editSubstitutionMapping(item: WholesalerSubstitution): void {
    this.dialogService
      .open(SubstitutionMappingDialogComponent, {
        context: { existingSubstitution: item },
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((payload: SubstitutionMappingDialogResult | null) => {
        if (!payload) {
          return;
        }
        this.spinner.show();
        const body: Record<string, unknown> = { strategy: payload.strategy };
        if (payload.toSupplierItemNumber !== undefined) {
          body['toSupplierItemNumber'] = payload.toSupplierItemNumber;
        }
        if (payload.toName !== undefined) {
          body['toName'] = payload.toName;
        }
        this.invoiceImportService.patchSubstitution(this.storeId, item._id, body).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastrSuccess('INVOICE_IMPORTS.SUB.SAVE_OK');
            this.loadSubstitutions();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', substitutionErrorMessage(this.translate, err), 'error');
          },
        });
      });
  }

  patchSubstitutionStrategy(item: WholesalerSubstitution, strategy: WholesalerSubstitutionStrategy): void {
    if (item.strategy === strategy) {
      return;
    }
    this.spinner.show();
    this.invoiceImportService.patchSubstitution(this.storeId, item._id, { strategy }).subscribe({
      next: () => {
        this.spinner.hide();
        item.strategy = strategy;
        this.toastrSuccess('INVOICE_IMPORTS.SETTINGS.SUB_MAPPING_UPDATED');
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', substitutionErrorMessage(this.translate, err), 'error');
        this.loadSubstitutions();
      },
    });
  }

  endSubstitution(item: WholesalerSubstitution): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.SETTINGS.SUB_END_CONFIRM_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.SETTINGS.SUB_END_CONFIRM_TEXT'),
      icon: 'warning',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService.deleteSubstitution(this.storeId, item._id).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastrSuccess('INVOICE_IMPORTS.SETTINGS.SUB_MAPPING_ENDED');
          this.loadSubstitutions();
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || err.message, 'error');
        },
      });
    });
  }

  substitutionTotalPages(): number {
    return Math.max(1, Math.ceil(this.substitutionTotal / this.substitutionLimit));
  }

  substitutionPrevPage(): void {
    if (this.substitutionPage <= 1) {
      return;
    }
    this.substitutionPage -= 1;
    this.loadSubstitutions();
  }

  substitutionNextPage(): void {
    if (this.substitutionPage >= this.substitutionTotalPages()) {
      return;
    }
    this.substitutionPage += 1;
    this.loadSubstitutions();
  }

  substitutionSourceLabel(item: WholesalerSubstitution): string {
    if (item.source === 'manual') {
      return this.translate.instant('INVOICE_IMPORTS.SETTINGS.SUB_SOURCE_MANUAL');
    }
    if (item.sourceInvoiceNumber) {
      return item.sourceInvoiceNumber;
    }
    return this.translate.instant('INVOICE_IMPORTS.SETTINGS.SUB_SOURCE_SUB');
  }

  cancel(): void {
    this.ref.close(false);
  }
}
